import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
    useMessagingSdk,
    useGetSelectedConversationId,
    useSetSelectedConversationId,
    useGetConversationFilter,
    NoConversationSelected,
    NotFoundNoticeUI,
    ConversationFolderLabel,
    MessagingSdk,
    RevokedPAJIMDialogUI,
    InitiateConversationError,
    InitiateConversationErrorReasonCode,
} from '@indeed/messaging-react';
import { useBreakpointIndex } from '@indeed/indeed-hook-library';

import {
    getEncryptedConversationId,
    getUnencryptedConversationId,
} from '../../utils/conversationIdUtils';
import JobDetailContainer from '../JobDetailsContainer';
import ConversationListContainer from '../ConversationListContainer/ConversationListContainer';
import {
    InboxPageWrapper,
    ConversationListPaneWrapper,
    DetailPaneWrapper,
    NoConversationSelectedWrapper,
} from './InboxPage.styled';

import '../../style/base.scss';
import { UserAgent } from 'rearchitecture/app/App';
import ServiceDegradationBanner from '../ServiceDegradationBanner';
import { ConversationPane } from '../ConversationPane';
import { useLogging } from '../../../utils/JSLoggingUtil';
import { getBrowserLogger } from '../../../browser-logger';
import {
    getConversationFolderLabelFromFilter,
    getFilterFromConversationFolderLabel,
} from 'rearchitecture/utils/filterUtils';

export interface TransientConversationProps {
    token: string;
    jobKey: string;
    advertiserKey?: string;
    candidateKey?: string;
    jobBoardPartnerId?: string;
    employerInfo?: {
        fullName?: string;
        role?: string;
    };
}
interface RouteParams {
    encryptedConversationId?: string;
}

const useUrlConversationIdOrToken = (): {
    conversationId: string | undefined;
    newConversationToken: string | undefined;
    successfullyDecrypted: boolean;
} => {
    const location = useLocation();
    const { encryptedConversationId: urlEncryptedConversationId } = useParams<RouteParams>();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

    if (urlEncryptedConversationId && urlEncryptedConversationId !== '') {
        try {
            return {
                conversationId: getUnencryptedConversationId(urlEncryptedConversationId),
                newConversationToken: undefined,
                successfullyDecrypted: true,
            };
        } catch (error) {
            // Enhanced error logging
            const errorMessage = `Conversation ID decryption failed: ${
                (error as Error).message
            }. From URL parameter: ${urlEncryptedConversationId}.`;
            const enhancedError = new Error(errorMessage);
            enhancedError.stack = (error as Error).stack;

            try {
                getBrowserLogger().logCaughtError(enhancedError);
            } catch (logError) {
                console.error('Failed to log conversation ID decryption error:', logError);
            }

            return {
                conversationId: undefined,
                newConversationToken: undefined,
                successfullyDecrypted: false,
            };
        }
    } else {
        if (location.pathname.includes('/conversations/new')) {
            const newConversationToken = params.get('token');
            if (newConversationToken) {
                return {
                    conversationId: undefined,
                    newConversationToken,
                    successfullyDecrypted: true,
                };
            } else {
                return {
                    conversationId: undefined,
                    newConversationToken: undefined,
                    successfullyDecrypted: false,
                };
            }
        }
    }

    return {
        conversationId: undefined,
        newConversationToken: undefined,
        successfullyDecrypted: true,
    };
};

const useUrlFolderLabel = (): ConversationFolderLabel | undefined => {
    const location = useLocation();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const filter = params.get('filter');
    return getConversationFolderLabelFromFilter(filter);
};

const useUrlNavigation = (
    sdk: MessagingSdk,
    urlConversationId: string | undefined,
    newConversationToken: string | undefined,
    urlFolderLabel: ConversationFolderLabel | undefined
): {
    conversationId: string | undefined;
    transientConversation?: TransientConversationProps;
    isPajimNotFound: boolean;
    showRevokedPAJIMDialog: boolean;
} => {
    const selectedConversationId = useGetSelectedConversationId();
    const setSelectedConversationId = useSetSelectedConversationId();
    const conversationFilter = useGetConversationFilter();
    const [transientConversation, setTransientConversation] = useState<
        TransientConversationProps | undefined
    >(undefined);
    const [isPajimInProgress, setIsPajimInProgress] = useState(!!newConversationToken);
    const [isPajimNotFound, setIsPajimNotFound] = useState(false);
    const [showRevokedPAJIMDialog, setShowRevokedPAJIMDialog] = useState(false);
    const { logPajimConversation } = useLogging();

    // Reset not-found state when user selects a different conversation
    useEffect(() => {
        if (selectedConversationId) {
            setIsPajimNotFound(false);
        }
    }, [selectedConversationId]);

    const shouldPreventUrlPush = useRef(false);
    const history = useHistory();
    const location = useLocation();

    // Update conversation ID based on URL.
    useEffect(() => {
        if (isPajimInProgress) {
            return;
        }

        if (history.action === 'POP') {
            // Avoid pushing URL if we popped history (e.g. refresh, back)
            shouldPreventUrlPush.current = true;
            setSelectedConversationId(urlConversationId);
        } else {
            // When a conversation is clicked, the conversation ID changes first.
            // This triggers a URL update (via the useEffect below), which then triggers this useEffect.
            //
            // To break this circular dependency where setting the conversation ID triggers
            // another conversation ID update, we skip the update if the ID hasn't changed.
            if (urlConversationId !== selectedConversationId) {
                shouldPreventUrlPush.current = true;
                setSelectedConversationId(urlConversationId);
            }
        }
    }, [urlConversationId, history.action, isPajimInProgress]);

    // Update the filter based on the URL.
    useEffect(() => {
        if (isPajimInProgress) {
            return;
        }

        if (history.action === 'POP') {
            // Avoid pushing URL if we popped history (e.g. refresh, back)
            shouldPreventUrlPush.current = true;
            sdk.setConversationFilter({ folderLabel: urlFolderLabel });
        } else {
            // When filter changes, the filter state changes first.
            // This triggers a URL update (via the useEffect below), which then triggers this useEffect.
            //
            // To break this circular update, we skip the update if the filter hasn't changed.
            if (urlFolderLabel !== conversationFilter?.folderLabel) {
                shouldPreventUrlPush.current = true;
                sdk.setConversationFilter({ folderLabel: urlFolderLabel });
            }
        }
    }, [urlFolderLabel, history.action, isPajimInProgress]);

    // Use our data to update the URL.
    useEffect(() => {
        // Do not update the URL if PAJIM token validation failed and no conversation selected
        if (isPajimNotFound && !selectedConversationId) {
            return;
        }

        if (isPajimInProgress) {
            return;
        }

        // Do not update the path if url triggered change in our data
        if (shouldPreventUrlPush.current) {
            shouldPreventUrlPush.current = false;
            return;
        }

        let newPath;
        if (selectedConversationId) {
            newPath = `/conversations/${getEncryptedConversationId(selectedConversationId)}`;
        } else if (location.pathname.replace(/\/$/, '') === '/conversations') {
            newPath = '/conversations';
        } else if (location.pathname.includes('/conversations/new')) {
            // Stay on /conversations/new when there's a token being validated
            // This prevents redirect when token is invalid or still being processed
            newPath = '/conversations/new';
        } else {
            newPath = '/';
        }

        const searchParams = new URLSearchParams(location.search);
        const filter: string | undefined = conversationFilter?.folderLabel
            ? getFilterFromConversationFolderLabel(conversationFilter.folderLabel)
            : undefined;
        if (filter) {
            searchParams.set('filter', filter);
        } else {
            searchParams.delete('filter');
        }

        const newSearch = searchParams.toString();

        // Only update URL if path or params changed
        if (location.pathname !== newPath || conversationFilter?.folderLabel !== urlFolderLabel) {
            history.push({
                pathname: newPath,
                search: newSearch,
            });
        }
    }, [
        history,
        selectedConversationId,
        conversationFilter,
        location.pathname,
        location.search,
        isPajimInProgress,
        isPajimNotFound,
    ]);

    // Initiate a new conversation by exchanging the pajim token for an encrypted conversation ID.
    // See README.rearchitecture.md#testing-pajim-links-enabling-msgs for details on generating and testing PAJIM tokens.
    useEffect(() => {
        if (!newConversationToken) {
            return () => {};
        }

        let isMounted = true;
        const initConversation = async () => {
            try {
                const response = await sdk.initiateConversation(newConversationToken);
                const {
                    data: { conversationSid, exists, token, metadata },
                } = response;
                if (isMounted) {
                    setIsPajimInProgress(false);

                    if (exists) {
                        logPajimConversation(conversationSid, metadata);
                        setTransientConversation(undefined);
                        setSelectedConversationId(getUnencryptedConversationId(conversationSid));
                        // Remove token from search params
                        const searchParams = new URLSearchParams(location.search);
                        searchParams.delete('token');
                        const cleanSearch = searchParams.toString();

                        history.replace({
                            pathname: `/conversations/${conversationSid}`,
                            search: cleanSearch ? `?${cleanSearch}` : '',
                        });
                    } else {
                        logPajimConversation(conversationSid, metadata);
                        setSelectedConversationId(undefined);
                        setTransientConversation({
                            token,
                            jobKey: metadata.jobKey,
                            advertiserKey: metadata.advertiserKey,
                            candidateKey: metadata.candidateKey,
                            jobBoardPartnerId: metadata.jobBoardPartnerId,
                        });
                    }
                }
            } catch (error) {
                if (isMounted) {
                    // TODO: How do we log errors to Sentry?
                    console.error('Failed  to initiate conversation:', error);
                    setIsPajimInProgress(false);

                    const isInvalidToken =
                        error instanceof InitiateConversationError && error.status === 400;

                    const isEmployerOptOutOrJobseekerNotEligible =
                        error instanceof InitiateConversationError &&
                        (error.reasonCode ===
                            InitiateConversationErrorReasonCode.EMPLOYER_OPT_OUT ||
                            error.reasonCode ===
                                InitiateConversationErrorReasonCode.JOBSEEKER_NOT_ELIGIBLE);

                    if (isInvalidToken) {
                        setIsPajimNotFound(true);
                    } else if (isEmployerOptOutOrJobseekerNotEligible) {
                        setShowRevokedPAJIMDialog(true);
                    } else {
                        // show RevokedPAJIMDialog for all other token API errors for now
                        setShowRevokedPAJIMDialog(true);
                    }
                }
            }
        };

        initConversation();

        return () => {
            isMounted = false;
        };
    }, [sdk, newConversationToken, location.search]);

    return {
        isPajimNotFound,
        conversationId: selectedConversationId,
        transientConversation,
        showRevokedPAJIMDialog,
    };
};

enum SinglePaneViewState {
    // Conversation list state is tracked separately using just conversationId
    Conversation = 'CONVERSATION',
    JobDetails = 'JOB_DETAILS',
}

/**
 * Handles the state of the single pane view. Returns isSinglePane=false when in multi-pane view.
 * @param userAgent - The user agent of the user.
 * @param conversationId - The ID of the conversation.
 * @returns The navigation handlers for the single pane view.
 */
const useSinglePaneViewState = (userAgent: UserAgent, conversationId: string | undefined) => {
    const { sdk } = useMessagingSdk();
    const [singlePaneViewState, setSinglePaneViewState] = useState<SinglePaneViewState>(
        SinglePaneViewState.Conversation
    );

    // Reset single pane view state to Conversation when conversation changes
    useEffect(() => {
        setSinglePaneViewState(SinglePaneViewState.Conversation);
    }, [conversationId]);

    const bpIndex = useBreakpointIndex();
    const isSinglePane = bpIndex <= 1; // Consider mobile (0) and small tablet (1) as single pane

    const conversationComponentNavigationHandlers = useMemo(() => {
        return {
            // On native apps, the app has its own back button.
            onBackClick: !userAgent.isNativeApp
                ? () => {
                      sdk.setSelectedConversationId(undefined);
                  }
                : undefined,
            // Only provide onNavigateToJobDetails in single pane view
            // In multi-pane view, job details are always visible so this handler is not needed
            onNavigateToJobDetails: isSinglePane
                ? () => {
                      setSinglePaneViewState(SinglePaneViewState.JobDetails);
                  }
                : undefined,
        };
    }, [userAgent, sdk, conversationId, isSinglePane]);

    const jobDetailsComponentNavigationHandlers = useMemo(() => {
        return {
            onBackClick: () => {
                setSinglePaneViewState(SinglePaneViewState.Conversation);
            },
        };
    }, []);

    return {
        isSinglePane,
        singlePaneViewState,
        conversationComponentNavigationHandlers,
        jobDetailsComponentNavigationHandlers,
    };
};

interface InboxPageProps {
    userAgent: UserAgent;
}

const NotFoundPage = React.memo(() => {
    const bpIndex = useBreakpointIndex();
    // Use breakpoint index to determine single pane (mobile/tablet) vs multi-pane (desktop)
    // This will re-render on window resize, which is acceptable for error pages
    const isSinglePane = bpIndex <= 1; // Consider mobile (0) and small tablet (1) as single pane

    return (
        <InboxPageWrapper data-testid="messaging-app" data-cy="messaging-app">
            {!isSinglePane && (
                <ConversationListPaneWrapper>
                    <ConversationListContainer />
                </ConversationListPaneWrapper>
            )}
            <NoConversationSelectedWrapper>
                <NotFoundNoticeUI />
            </NoConversationSelectedWrapper>
        </InboxPageWrapper>
    );
});

// Hook to detect and fix HTML-encoded URLs BEFORE useParams processes them
const useHtmlUrlCleaner = () => {
    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
        // Construct URL using React Router location for Storybook compatibility
        // In production: same result as window.location.href
        // In Storybook: uses MemoryRouter pathname/search instead of iframe URL
        const currentUrl = `${window.location.origin}${location.pathname}${location.search}`;

        // First, try to extract and decrypt the conversation ID from the path
        const conversationMatch = location.pathname.match(/\/conversations\/([^?]+)/);
        if (!conversationMatch) {
            return;
        }

        const encryptedConversationId = conversationMatch[1];

        try {
            // Try to decrypt the conversation ID - if this succeeds, no HTML decoding needed
            getUnencryptedConversationId(encryptedConversationId);
            return; // Decryption succeeded, no need for HTML decoding
        } catch (decryptionError) {
            // Decryption failed, now try HTML decoding as fallback
            try {
                // Clean HTML entities in both path and search parameters
                const decodedPath = location.pathname
                    .replace(/&#61;/g, '=')
                    .replace(/&#63;/g, '?')
                    .replace(/&amp;/g, '&');
                const decodedSearch = location.search.replace(/&#63;/g, '?').replace(/&amp;/g, '&');

                if (decodedPath !== location.pathname || decodedSearch !== location.search) {
                    try {
                        getBrowserLogger().logSignalsDebug(
                            'Successfully cleaned HTML-encoded URL (rearchitecture)',
                            '',
                            JSON.stringify({
                                originalUrl: currentUrl,
                                originalPath: location.pathname,
                                originalSearch: location.search,
                                decodedPath,
                                decodedSearch,
                                decryptionFailed: true,
                                decryptionError: (decryptionError as Error).message,
                                timestamp: new Date().toISOString(),
                            })
                        );
                    } catch {
                        // Silently fail if logging is not available
                    }

                    // Use React Router's replace method
                    history.replace({
                        pathname: decodedPath,
                        search: decodedSearch,
                        hash: location.hash,
                    });
                }
            } catch (htmlDecodeError) {
                // Log error but don't crash the app
                try {
                    getBrowserLogger().logCaughtError(
                        new Error(
                            `Failed to clean HTML-encoded URL after decryption failed. Decryption error: ${
                                (decryptionError as Error).message
                            }, HTML decode error: ${(htmlDecodeError as Error).message}`
                        )
                    );
                } catch {
                    // Silently fail if logging is not available
                }
            }
        }
    }, [location.pathname, location.search, history]);
};

const InboxPage = ({ userAgent }: InboxPageProps) => {
    const { sdk } = useMessagingSdk();

    // Clean HTML-encoded URLs BEFORE useParams processes them
    useHtmlUrlCleaner();

    const {
        conversationId: urlConversationId,
        newConversationToken,
        successfullyDecrypted,
    } = useUrlConversationIdOrToken();

    useEffect(() => {
        sdk.enableRealtime();
        return () => sdk.disableRealtime();
    }, [sdk]);

    return (
        <InboxPageWithSuccessfullyDecryptedUrlConversationId
            urlConversationId={urlConversationId}
            newConversationToken={newConversationToken}
            userAgent={userAgent}
            successfullyDecrypted={successfullyDecrypted}
        />
    );
};

const InboxPageWithSuccessfullyDecryptedUrlConversationId = ({
    urlConversationId,
    newConversationToken,
    userAgent,
    successfullyDecrypted,
}: {
    urlConversationId: string | undefined;
    newConversationToken: string | undefined;
    userAgent: UserAgent;
    successfullyDecrypted: boolean;
}) => {
    const { sdk } = useMessagingSdk();
    const urlFolderLabel = useUrlFolderLabel();
    const { conversationId, isPajimNotFound, transientConversation, showRevokedPAJIMDialog } =
        useUrlNavigation(sdk, urlConversationId, newConversationToken, urlFolderLabel);

    const {
        isSinglePane,
        singlePaneViewState,
        conversationComponentNavigationHandlers,
        jobDetailsComponentNavigationHandlers,
    } = useSinglePaneViewState(userAgent, conversationId);

    // Calculate GNAV visibility
    const shouldHideGnav =
        !isPajimNotFound &&
        isSinglePane &&
        singlePaneViewState === SinglePaneViewState.Conversation &&
        Boolean(conversationId || transientConversation);

    // Control GNAV visibility - now called every render
    useEffect(() => {
        const gnav = document.getElementById('msgwIndeedNavigation');
        if (gnav !== null) {
            gnav.setAttribute('aria-hidden', String(shouldHideGnav));
            gnav.style.display = shouldHideGnav ? 'none' : 'unset';
        }
    }, [shouldHideGnav]);

    if (isPajimNotFound || (!successfullyDecrypted && !conversationId)) {
        return <NotFoundPage />;
    }

    const shouldShowConversationListPane =
        // Show on desktop
        !isSinglePane ||
        // Show on single pane if no conversation is selected
        (isSinglePane && !conversationId);
    const shouldShowConversationPane =
        // Show on desktop
        !isSinglePane ||
        // Show on single pane if the conversation is selected
        (isSinglePane && singlePaneViewState === SinglePaneViewState.Conversation);
    const shouldShowJobDetailsPane =
        // Show on desktop
        !isSinglePane ||
        // Show on single pane if the job details are selected
        (isSinglePane && singlePaneViewState === SinglePaneViewState.JobDetails);

    return (
        <>
            <ServiceDegradationBanner />
            <InboxPageWrapper data-testid="messaging-app" data-cy="messaging-app">
                {shouldShowConversationListPane && (
                    <ConversationListPaneWrapper>
                        <ConversationListContainer />
                    </ConversationListPaneWrapper>
                )}
                {(conversationId || transientConversation) && (
                    <>
                        {shouldShowConversationPane && (
                            <ConversationPane
                                conversationId={conversationId}
                                transientConversation={transientConversation}
                                isSinglePane={isSinglePane}
                                navigationHandlers={conversationComponentNavigationHandlers}
                            />
                        )}
                        {shouldShowJobDetailsPane && (
                            <DetailPaneWrapper>
                                <JobDetailContainer
                                    conversationId={conversationId}
                                    jobKey={transientConversation?.jobKey}
                                    isSinglePane={isSinglePane}
                                    navigationHandlers={jobDetailsComponentNavigationHandlers}
                                />
                            </DetailPaneWrapper>
                        )}
                    </>
                )}
                {!conversationId && !transientConversation && !isSinglePane && (
                    <NoConversationSelectedWrapper>
                        <NoConversationSelected />
                    </NoConversationSelectedWrapper>
                )}
                {showRevokedPAJIMDialog && <RevokedPAJIMDialogUI />}
            </InboxPageWrapper>
        </>
    );
};

export default InboxPage;
